import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { doc, updateDoc, serverTimestamp, arrayUnion, arrayRemove, Timestamp } from "firebase/firestore";
import { StorageError } from "firebase/storage";
import { firestore } from "@/base";
import useUserStore from "@/modules/users/store";
import { getCurrentUser } from "@/services/auth";
import { captureException } from "@/services/log";
import { TEMPLATES_QUERY_KEY } from "./useTemplatesManager";
import type { TemplateImage, TemplateListItem } from "../types/template";
import { uploadTemplateImage, deleteTemplateImage, validateImageFile } from "../utils/storage";

/**
 * Generate a unique ID for images
 */
function generateImageId(): string {
  return `img_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Normalize image name to be URL-safe and variable-friendly
 * @param name - Original name
 * @returns Normalized name
 */
function normalizeImageName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replaceAll(/[\u0300-\u036F]/g, "") // Remove accents
    .replaceAll(/[^\w-]/g, "_") // Replace non-alphanumeric with underscore
    .replaceAll(/_+/g, "_") // Collapse multiple underscores
    .replaceAll(/^_|_$/g, ""); // Trim underscores
}

interface UseTemplateImagesProperties {
  templateId: string;
  currentImages?: TemplateImage[];
}

/**
 * Hook para gerenciar imagens de template
 *
 * Features:
 * - Adicionar imagem via URL externa
 * - Upload de imagem para Firebase Storage
 * - Remover imagem (com cleanup do Storage)
 * - Validação de tipo e tamanho
 * - Optimistic updates
 * @param root0
 * @param root0.templateId
 * @param root0.currentImages
 */
export function useTemplateImages({ templateId, currentImages = [] }: UseTemplateImagesProperties) {
  const queryClient = useQueryClient();
  const licenciadaId = useUserStore((state) => state.activeLicenciadaId);
  const currentUser = getCurrentUser();

  // Mutation: Add image via URL
  const addUrlMutation = useMutation({
    mutationFn: async ({ nome, url }: { nome: string; url: string }) => {
      if (!licenciadaId) throw new Error("Licenciada não selecionada");
      if (!currentUser) throw new Error("Usuário não autenticado");

      const normalizedName = normalizeImageName(nome);

      // Check for duplicate name
      const existingImage = currentImages.find((img) => img.nome === normalizedName);
      if (existingImage) {
        throw new Error(`Já existe uma imagem com o nome "${normalizedName}"`);
      }

      const newImage: TemplateImage = {
        id: generateImageId(),
        nome: normalizedName,
        url,
        tipo: "url",
        createdAt: Timestamp.now()
      };

      const templateReference = doc(firestore, "licenciadas", licenciadaId, "templates_relatorio", templateId);

      await updateDoc(templateReference, {
        imagens: arrayUnion(newImage),
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      });

      return newImage;
    },
    onMutate: async ({ nome, url }) => {
      await queryClient.cancelQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });

      const previousTemplates = queryClient.getQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId]);

      const normalizedName = normalizeImageName(nome);
      const newImage: TemplateImage = {
        id: generateImageId(),
        nome: normalizedName,
        url,
        tipo: "url",
        createdAt: Timestamp.now()
      };

      queryClient.setQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId], (old) => {
        if (!old) return old;
        return old.map((template) =>
          template.id === templateId
            ? { ...template, imagens: [...(template.imagens || []), newImage], updatedAt: new Date() }
            : template
        );
      });

      return { previousTemplates };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousTemplates) {
        queryClient.setQueryData([TEMPLATES_QUERY_KEY, licenciadaId], context.previousTemplates);
      }
      captureException(error, false);
      notifications.show({
        title: "Erro",
        message: error.message || "Erro ao adicionar imagem",
        color: "red"
      });
    },
    onSuccess: (newImage) => {
      notifications.show({
        title: "Sucesso",
        message: `Imagem "${newImage.nome}" adicionada`,
        color: "green"
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });
    }
  });

  // Mutation: Upload image to Storage
  const uploadMutation = useMutation({
    mutationFn: async ({ nome, file }: { nome: string; file: File }) => {
      if (!licenciadaId) throw new Error("Licenciada não selecionada");
      if (!currentUser) throw new Error("Usuário não autenticado");

      // Validate file
      const validation = validateImageFile(file);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      const normalizedName = normalizeImageName(nome);

      // Check for duplicate name
      const existingImage = currentImages.find((img) => img.nome === normalizedName);
      if (existingImage) {
        throw new Error(`Já existe uma imagem com o nome "${normalizedName}"`);
      }

      const imageId = generateImageId();

      // Upload to Storage
      const { url, storagePath } = await uploadTemplateImage(file, licenciadaId, templateId, imageId);

      const newImage: TemplateImage = {
        id: imageId,
        nome: normalizedName,
        url,
        tipo: "storage",
        storagePath,
        createdAt: Timestamp.now()
      };

      // Save to Firestore
      const templateReference = doc(firestore, "licenciadas", licenciadaId, "templates_relatorio", templateId);

      await updateDoc(templateReference, {
        imagens: arrayUnion(newImage),
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      });

      return newImage;
    },
    onError: (error: Error) => {
      captureException(error, false);
      notifications.show({
        title: "Erro no upload",
        message: error.message || "Erro ao fazer upload da imagem",
        color: "red"
      });
    },
    onSuccess: (newImage) => {
      notifications.show({
        title: "Upload concluído",
        message: `Imagem "${newImage.nome}" enviada com sucesso`,
        color: "green"
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });
    }
  });

  // Mutation: Remove image
  const removeMutation = useMutation({
    mutationFn: async (imageId: string) => {
      if (!licenciadaId) throw new Error("Licenciada não selecionada");
      if (!currentUser) throw new Error("Usuário não autenticado");

      const imageToRemove = currentImages.find((img) => img.id === imageId);
      if (!imageToRemove) {
        throw new Error("Imagem não encontrada");
      }

      // If it's a storage image, delete from Storage first
      if (imageToRemove.tipo === "storage" && imageToRemove.storagePath) {
        try {
          await deleteTemplateImage(imageToRemove.storagePath);
        } catch (storageError) {
          // Only accept object-not-found as non-critical (file already deleted)
          if (storageError instanceof StorageError && storageError.code === "storage/object-not-found") {
            console.info("Storage file already deleted:", imageToRemove.storagePath);
          } else {
            // Log real errors for monitoring
            captureException(storageError instanceof Error ? storageError : new Error(String(storageError)), false);
            // Fail the mutation - don't proceed to Firestore deletion
            throw new Error("Falha ao deletar imagem do armazenamento. A imagem não foi removida. Tente novamente.");
          }
        }
      }

      // Remove from Firestore
      const templateReference = doc(firestore, "licenciadas", licenciadaId, "templates_relatorio", templateId);

      await updateDoc(templateReference, {
        imagens: arrayRemove(imageToRemove),
        updatedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      });

      return imageToRemove;
    },
    onMutate: async (imageId) => {
      await queryClient.cancelQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });

      const previousTemplates = queryClient.getQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId]);

      queryClient.setQueryData<TemplateListItem[]>([TEMPLATES_QUERY_KEY, licenciadaId], (old) => {
        if (!old) return old;
        return old.map((template) =>
          template.id === templateId
            ? {
                ...template,
                imagens: (template.imagens || []).filter((img) => img.id !== imageId),
                updatedAt: new Date()
              }
            : template
        );
      });

      return { previousTemplates };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousTemplates) {
        queryClient.setQueryData([TEMPLATES_QUERY_KEY, licenciadaId], context.previousTemplates);
      }
      captureException(error, false);
      notifications.show({
        title: "Erro",
        message: error.message || "Erro ao remover imagem",
        color: "red"
      });
    },
    onSuccess: (removedImage) => {
      notifications.show({
        title: "Sucesso",
        message: `Imagem "${removedImage.nome}" removida`,
        color: "green"
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [TEMPLATES_QUERY_KEY, licenciadaId] });
    }
  });

  return {
    // Current images
    images: currentImages,

    // Actions
    addImageUrl: addUrlMutation.mutateAsync,
    uploadImage: uploadMutation.mutateAsync,
    removeImage: removeMutation.mutateAsync,

    // Loading states
    isAddingUrl: addUrlMutation.isPending,
    isUploading: uploadMutation.isPending,
    isRemoving: removeMutation.isPending,
    isLoading: addUrlMutation.isPending || uploadMutation.isPending || removeMutation.isPending,

    // Utilities
    validateFile: validateImageFile
  };
}
